import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Compass, Languages, Church, Droplets } from "lucide-react";
import { fetchCountries } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { trackEvent } from "@/lib/analytics";
import { INTRO, REGIONS } from "@/lib/introContent";
import RegionsMap from "@/components/RegionsMap";

const KEY_ICONS = [Languages, Church, Droplets];

export default function IntroSection() {
  const { lang } = useLang();
  const c = INTRO[lang] || INTRO.en;
  const navigate = useNavigate();
  const { data: countries = [] } = useQuery({ queryKey: ["countries"], queryFn: fetchCountries });
  const [active, setActive] = useState(null);
  const { hash } = useLocation();
  useEffect(() => {
    if (hash === "#intro") document.getElementById("intro")?.scrollIntoView({ block: "start" });
  }, [hash]);
  const byCode = Object.fromEntries(countries.map((k) => [k.code, k]));
  const openCountry = (code) => { trackEvent("intro_country_open", { country_code: code }); navigate(`/?c=${code}`); };
  const toggle = (id) => { setActive((a) => (a === id ? null : id)); trackEvent("intro_region", { region: id }); };

  return (
    <section id="intro" className="space-y-6 scroll-mt-24" data-testid="intro-section">
      <div>
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-ochre font-semibold mb-2"><Compass className="w-4 h-4" /> {c.kicker}</div>
        <h2 className="text-2xl sm:text-3xl font-display font-bold text-ink leading-tight" data-testid="intro-title">{c.title}</h2>
        <p className="mt-3 text-base leading-relaxed text-ink/85 max-w-3xl" data-testid="intro-lead">{c.lead}</p>
      </div>

      <div className="grid lg:grid-cols-[1.5fr_1fr] gap-5 items-start">
        <div onMouseLeave={() => setActive(null)}>
          <RegionsMap countries={countries} active={active} onRegion={setActive} onCountry={openCountry} />
          <p className="text-[11px] text-muted-foreground mt-2 text-center">{c.map_hint}</p>
        </div>
        <ol className="space-y-2" data-testid="intro-regions">
          {REGIONS.map((r, i) => (
            <RegionRow key={r.id} region={r} index={i} text={c.regions[r.id]} active={active} onToggle={toggle} byCode={byCode} lang={lang} onCountry={openCountry} />
          ))}
        </ol>
      </div>

      <div>
        <h3 className="text-[10px] uppercase tracking-[0.18em] text-ochre font-semibold mb-3">{c.keys_title}</h3>
        <div className="grid md:grid-cols-3 gap-3" data-testid="intro-keys">
          {c.keys.map((k, i) => {
            const Icon = KEY_ICONS[i];
            return (
              <article key={k.h} className="rounded-2xl border border-stoneLine bg-card p-4 sm:p-5" data-testid={`intro-key-${i}`}>
                <span className="w-9 h-9 rounded-full bg-ochre/10 text-ochre flex items-center justify-center mb-3"><Icon className="w-4 h-4" /></span>
                <h4 className="font-display font-bold text-base text-ink leading-snug">{k.h}</h4>
                <p className="text-sm leading-relaxed text-ink/80 mt-1.5">{k.p}</p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function RegionRow({ region, index, text, active, onToggle, byCode, lang, onCountry }) {
  const open = active === region.id;
  return (
    <li className={`rounded-2xl border transition-colors ${open ? "border-ochre bg-card" : "border-stoneLine bg-card/60 hover:border-ochre/50"}`} data-testid={`intro-region-${region.id}`}>
      <button type="button" onClick={() => onToggle(region.id)} className="w-full text-start flex items-center gap-3 p-3" aria-expanded={open} data-testid={`intro-region-toggle-${region.id}`}>
        <span className="w-7 h-7 rounded-full text-white text-xs font-bold flex items-center justify-center shrink-0" style={{ background: region.color }}>{index + 1}</span>
        <span className="min-w-0 flex-1">
          <span className="block font-semibold text-ink text-sm leading-tight">{text.name}</span>
          <span className="block text-[11px] text-muted-foreground">{text.sub} · {region.codes.length}</span>
        </span>
      </button>
      <div className={`flex flex-wrap gap-1.5 px-3 overflow-hidden transition-all duration-300 ${open ? "max-h-40 pb-3" : "max-h-0"}`}>
        {region.codes.map((code) => byCode[code] && (
          <button key={code} type="button" onClick={() => onCountry(code)} className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full border border-stoneLine bg-card hover:border-ochre hover:text-ochre transition-colors" data-testid={`intro-country-${code}`}>
            <img src={byCode[code].flag_url} alt="" className="w-4 h-3 object-cover rounded-[2px] border border-stoneLine" /> {byCode[code].short_names[lang] || byCode[code].short_names.en}
          </button>
        ))}
      </div>
    </li>
  );
}
